/**
 * Saved sketches — service layer for the /sketch trace tool.
 *
 * Backed by api/sketches.js (Supabase `user_sketches` + the public `sketches` Storage bucket),
 * scoped server-side to the authenticated Clerk user. The sketch document is a single JSON
 * file (serialization v1 — layer PNGs, measurements, text objects, scale, viewport) uploaded
 * via signed-URL PUT: it's multi-MB, so it never passes through the Vercel function body.
 *
 * SketchPage sets window.__clerkGetToken itself (the /sketch route doesn't mount OutlineView).
 */

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/sketches' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Sketches request failed (' + res.status + ')'))
  return data
}

/** All of the current user's sketches, newest first:
 *  [{ id, name, thumbUrl, updatedAt, projectId, projectName }] — projectId null = the Misc. folder. */
export function listSketches() {
  return call('GET', null).then(d => d.sketches || [])
}

/** Load one sketch: metadata + the parsed serialization-v1 document. */
export async function getSketch(sketchId) {
  const { sketch } = await call('GET', null, 'id=' + encodeURIComponent(sketchId))
  // Cache-bust — the bucket is public/CDN-cached and autosave rewrites the same path.
  const res = await fetch(sketch.fileUrl + '?t=' + Date.now())
  if (!res.ok) throw new Error('Could not load the sketch file (' + res.status + ')')
  const doc = await res.json()
  return { ...sketch, doc }
}

async function putSigned(signedUrl, body, contentType) {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'x-upsert': 'true' },
    body,
  })
  if (!res.ok) throw new Error('Upload failed (' + res.status + ')')
}

/**
 * Save (create or overwrite) a sketch. `sketchId` null = create; returns the id either way.
 * jsonBlob = the serialized document, thumbBlob = small PNG preview (may be null).
 */
export async function saveSketch(sketchId, name, jsonBlob, thumbBlob, project) {
  // project: { id, name } = that project's folder, null = Misc., undefined = leave the folder as is
  const folder = project === undefined ? {} : { project: project && project.id ? { id: project.id, name: project.name || '' } : null }
  const { sketchId: id, fileUpload, thumbUpload } = await call('POST', { action: 'saveUrl', sketchId: sketchId || undefined, name, ...folder })
  await putSigned(fileUpload.signedUrl, jsonBlob, 'application/json')
  if (thumbBlob) await putSigned(thumbUpload.signedUrl, thumbBlob, 'image/png')
  await call('POST', { action: 'confirm', sketchId: id, name, ...folder })
  return id
}

/** Move a sketch to a project's folder ({ id, name }) or to Misc. (null). */
export function moveSketch(sketchId, project) {
  return call('POST', { action: 'move', sketchId, project: project && project.id ? { id: project.id, name: project.name || '' } : null }).then(d => d.sketch)
}

export function renameSketch(sketchId, name) {
  return call('POST', { action: 'rename', sketchId, name }).then(d => d.sketch)
}

export function deleteSketch(sketchId) {
  return call('POST', { action: 'delete', sketchId }).then(d => d.ok)
}
